import {
    cloneEmailTemplateThunk,
    deleteEmailTemplateThunk,
    fetchEmailTemplatesThunk
} from '@spike/email-marketing-action';
import { EmailTemplate } from '@spike/email-marketing-model';
import DeleteModal from 'components/UI/V3/Modals/DeleteModal/DeleteModal';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { EmailTemplateCreatePath, EmailTemplateEditPath } from 'routes/Paths';
import { RootState } from 'store';
import EmailTemplatePreviewModal from './Gallery/EmailTemplatePreviewModal';
import EmailTemplatesGallery from './Gallery/EmailTemplatesGallery';
import EmailTemplatesHeader from './Header/EmailTemplatesHeader';
import EmailTemplatesList from './List/EmailTemplatesList';
import EmailTemplatesEmptyState from './UI/EmailTemplatesEmptyState';
import styles from './EmailTemplates.module.scss';

export type EmailTemplatesView = 'list' | 'gallery';

interface EmailTemplatesProps {
    view: EmailTemplatesView;
    onStartWithTemplate: () => void;
    onCloseGallery: () => void;
}

const PAGE_SIZE = 10;

const EmailTemplates: FunctionComponent<EmailTemplatesProps> = ({ view, onStartWithTemplate, onCloseGallery }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const apiClientWrapper = useApiClientWrapper();

    const list = useSelector((state: RootState) => state.emailTemplates.list);

    const [page, setPage] = useState<number>(1);
    const [searchText, setSearchText] = useState<string>('');
    const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
    const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
    // Gates the empty-state flash on first mount: the slice's loading flag is
    // false until the dispatch reaches the reducer (one render later), so the
    // empty state would otherwise render for one frame before the spinner.
    const [initialFetchInFlight, setInitialFetchInFlight] = useState<boolean>(true);

    useEffect(() => {
        if (view === 'list') {
            setInitialFetchInFlight(true);
            (dispatch(fetchEmailTemplatesThunk(apiClientWrapper, 'own')) as unknown as Promise<unknown>).finally(() =>
                setInitialFetchInFlight(false)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    // Defensive client-side filter: even if the slice was last populated by the
    // gallery (no scope, includes system templates), the saved list never shows
    // system templates. Backend's `scope=own` already excludes them, this is a
    // double safeguard during the brief moment between mount and first fetch.
    const ownTemplates = useMemo(() => list.filter(template => template.editable === true), [list]);

    const filteredTemplates = useMemo(() => {
        if (searchText.length === 0) {
            return ownTemplates;
        }
        return ownTemplates.filter(template => template.name.toLowerCase().includes(searchText.toLowerCase()));
    }, [searchText, ownTemplates]);

    const paginatedTemplates = useMemo(
        () => filteredTemplates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredTemplates, page]
    );

    const totalPages = useMemo(() => Math.ceil(filteredTemplates.length / PAGE_SIZE), [filteredTemplates]);

    const handleDuplicateTemplate = (template: EmailTemplate) => {
        if (typeof template.id !== 'number') return;
        dispatch(cloneEmailTemplateThunk(apiClientWrapper, template.id));
    };

    /**
     * "Use Template" from the standalone gallery — semantics here are
     * "start a new custom template from this system one":
     *   - Clone it (BE creates a new own template seeded with the system's
     *     `design_json` + `html`).
     *   - On success, navigate to the editor of the new clone so the user
     *     can customise it.
     *   - On failure, the thunk's `alertErrorHandler` surfaced the toast;
     *     the user stays on the gallery to retry.
     *
     * Different from the wizard's "Use Template", which only sets
     * `emailTemplateId` on the campaign (no clone, no navigation).
     */
    const handleUseSystemTemplate = async (template: EmailTemplate) => {
        if (typeof template.id !== 'number') return;
        const cloned = await (dispatch(
            cloneEmailTemplateThunk(apiClientWrapper, template.id)
        ) as unknown as Promise<EmailTemplate | null>);
        if (cloned && typeof cloned.id === 'number') {
            history.push(EmailTemplateEditPath.replace(':id', String(cloned.id)));
        }
    };

    const handleDeleteRequest = (template: EmailTemplate) => {
        setTemplateToDelete(template);
    };

    const handleConfirmDelete = () => {
        if (!templateToDelete || typeof templateToDelete.id !== 'number') {
            setTemplateToDelete(null);
            return;
        }
        dispatch(deleteEmailTemplateThunk(apiClientWrapper, templateToDelete.id));
        setTemplateToDelete(null);
    };

    const handleCancelDelete = () => {
        setTemplateToDelete(null);
    };

    const handleStartFromScratch = () => {
        history.push(EmailTemplateCreatePath);
    };

    if (view === 'gallery') {
        return (
            <div className={styles.root}>
                <EmailTemplatesGallery
                    onClose={onCloseGallery}
                    onSelectTemplate={handleUseSystemTemplate}
                    onPreviewTemplate={setPreviewTemplate}
                />
                <EmailTemplatePreviewModal
                    template={previewTemplate}
                    onClose={() => setPreviewTemplate(null)}
                />
            </div>
        );
    }

    // Full-screen spinner during the initial fetch so the layout never flashes
    // through an intermediate state. Without this early return the header
    // would render in FULL mode during the fetch (initialFetchInFlight=true
    // → minimal=false), then collapse to minimal once the fetch settled with
    // zero own templates — a visible jump. Placed AFTER the gallery early
    // return so opening straight into gallery view (where the list fetch
    // effect never runs, so `initialFetchInFlight` stays true forever) still
    // renders the gallery correctly.
    if (initialFetchInFlight) {
        return (
            <div className={styles.root}>
                <div className={styles.loaderContainer}>
                    <Spinner />
                </div>
            </div>
        );
    }

    // Render the friendly EmptyState only when there are zero own templates AND
    // the user is not actively searching. A non-empty search with zero matches
    // should keep the list visible (its inline "No templates found" cell is the
    // correct affordance for "no results", not "no data yet").
    const hasTemplates = ownTemplates.length > 0;
    const isSearching = searchText.length > 0;
    const showEmptyState = !hasTemplates && !isSearching;

    return (
        <div className={styles.root}>
            <EmailTemplatesHeader
                searchText={searchText}
                onChangeSearchText={setSearchText}
                onStartWithTemplate={onStartWithTemplate}
                onStartFromScratch={handleStartFromScratch}
                minimal={showEmptyState}
            />

            {showEmptyState ? (
                <EmailTemplatesEmptyState
                    onStartWithTemplate={onStartWithTemplate}
                    onStartFromScratch={handleStartFromScratch}
                />
            ) : (
                <EmailTemplatesList
                    templates={paginatedTemplates}
                    page={page}
                    totalPages={totalPages}
                    onChangePage={setPage}
                    onDuplicateTemplate={handleDuplicateTemplate}
                    onDeleteTemplate={handleDeleteRequest}
                />
            )}

            {templateToDelete && (
                <DeleteModal
                    title="Delete Template"
                    entityName={templateToDelete.name}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
};

export default EmailTemplates;
